(() => {
  const doc = document;

  /* Sticky header shadow */
  const headerSection = doc.querySelector('.shopify-section.header-section');
  if (headerSection) {
    const updateStuck = () => {
      headerSection.classList.toggle('is-stuck', window.scrollY > 8);
    };
    updateStuck();
    window.addEventListener('scroll', updateStuck, { passive: true });
  }

  /* Mobile nav */
  const toggle = doc.querySelector('[data-menu-toggle]');
  const nav = doc.querySelector('[data-site-nav]');
  if (toggle && nav) {
    const setOpen = (open) => {
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      doc.body.style.overflow = open ? 'hidden' : '';
    };

    toggle.addEventListener('click', () => {
      setOpen(!nav.classList.contains('is-open'));
    });

    nav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => setOpen(false));
    });

    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });
  }

  /* Product gallery (mobile slideshow dots + scroll sync) */
  doc.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('.product__media-track');
    const slides = [...gallery.querySelectorAll('[data-product-slide]')];
    const dots = [...gallery.querySelectorAll('[data-product-dot]')];
    if (!track || slides.length < 2) return;

    const setActive = (index) => {
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        slides[i].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        setActive(i);
      });
    });

    track.addEventListener(
      'scroll',
      () => {
        const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        setActive(Math.min(Math.max(index, 0), slides.length - 1));
      },
      { passive: true }
    );
  });

  /* Hero slideshow */
  doc.querySelectorAll('[data-hero-slideshow]').forEach((hero) => {
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-hero-dot]')];
    if (slides.length < 2) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = hero.dataset.autoplay === 'true' && !prefersReducedMotion;
    const speed = (Number(hero.dataset.autoplaySpeed) || 5) * 1000;
    let index = 0;
    let timer = null;

    const setSlide = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;

      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    };

    const next = () => setSlide(index + 1);

    const start = () => {
      if (!autoplay) return;
      stop();
      timer = window.setInterval(next, speed);
    };

    const stop = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        setSlide(i);
        start();
      });
    });

    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);
    hero.addEventListener('focusin', stop);
    hero.addEventListener('focusout', (event) => {
      if (!hero.contains(event.relatedTarget)) start();
    });

    start();
  });

  /* Collection infinite scroll */
  const infiniteRoot = doc.querySelector('[data-infinite-scroll]');
  if (infiniteRoot) {
    const grid = infiniteRoot.querySelector('[data-collection-grid]');
    const sentinel = infiniteRoot.querySelector('[data-load-more-sentinel]');
    const status = infiniteRoot.querySelector('[data-load-more-status]');
    let nextUrl = infiniteRoot.dataset.nextUrl || '';
    let loading = false;

    const setStatus = (visible) => {
      if (status) status.hidden = !visible;
    };

    const loadNext = async () => {
      if (!nextUrl || loading) return;
      loading = true;
      setStatus(true);

      try {
        const response = await fetch(nextUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Could not load more products');

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = parsed.querySelector('[data-collection-grid]');
        const incomingRoot = parsed.querySelector('[data-infinite-scroll]');

        if (incoming && grid) {
          [...incoming.children].forEach((item) => grid.appendChild(item));
        }

        nextUrl = incomingRoot && incomingRoot.dataset.nextUrl ? incomingRoot.dataset.nextUrl : '';
        if (incomingRoot) infiniteRoot.dataset.nextUrl = nextUrl;
        if (!nextUrl && sentinel) sentinel.remove();
      } catch (error) {
        nextUrl = '';
        doc.documentElement.classList.remove('has-infinite-scroll');
      } finally {
        loading = false;
        setStatus(false);
      }
    };

    if (sentinel && nextUrl && 'IntersectionObserver' in window) {
      doc.documentElement.classList.add('has-infinite-scroll');
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) loadNext();
        },
        { rootMargin: '0px 0px 480px 0px' }
      );
      observer.observe(sentinel);
    }
  }

  /* Scroll reveal */
  const reveals = doc.querySelectorAll('.reveal');
  if (reveals.length) {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
      );
      reveals.forEach((el) => observer.observe(el));
    } else {
      reveals.forEach((el) => el.classList.add('is-visible'));
    }
  }
})();
